import React from 'react';

const controlCls = 'h-8 px-2.5 rounded-md border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--border-strong)] disabled:opacity-40';
const sectionLabelCls = 'text-[11px] font-medium text-[color:var(--text-muted)]';

/**
 * Declared variables — an optional default per variable.
 *
 * The variable NAMES are all the engine needs; they're auto-detected from the
 * messages and never typed here. The one thing an author sets is a default: at
 * compile time each `{{variable}}` takes the caller's value, or — when the
 * caller omits it — this default, or empty if there's no default.
 *
 * A missing value NEVER fails. There is deliberately no "required" flag, and
 * this panel briefly shipped one: it gave the author a decision to get wrong
 * and the caller a crash where an empty render would do. The default is the
 * whole mechanism — set it to whatever an omitted call should produce, empty
 * included.
 *
 * A *placeholder* (a list of messages injected at runtime) is declared by
 * adding a placeholder in the Messages section and is shown here read-only.
 *
 * A *section* variable — a name used as {{#x}}…{{/x}} — is inferred as `json`
 * (a list to loop over, or an object). It's provided at call time, so a text
 * default doesn't apply; it's shown read-only like a placeholder.
 */
function TypeChip({ type }) {
  return (
    <span className="shrink-0 rounded border border-[color:var(--border-default)] px-1.5 py-0.5 text-[10.5px] font-medium text-[color:var(--text-muted)]">
      {type}
    </span>
  );
}

const GRID = 'grid grid-cols-[1fr_1.4fr] items-center gap-3';

export default function VariablesPanel({ variables, onChange, disabled, innerNames }) {
  const set = (i, next) => onChange(variables.map((v, j) => (j === i ? next : v)));

  if (!variables.length) {
    return (
      <p className="text-[12px] text-[color:var(--text-muted)]">
        No variables. Write <code className="font-mono">{'{{name}}'}</code> in a message.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className={`${GRID} px-0.5`}>
        <span className={sectionLabelCls}>Name</span>
        <span className={sectionLabelCls}>Default</span>
      </div>

      {variables.map((v, i) => {
        // Structured variables — a placeholder (message list) or a section
        // variable (json list/object). Both are supplied at call time, so a text
        // default doesn't apply; show the inferred type and the note instead.
        if (v.type === 'messages' || v.type === 'json') {
          const note = v.type === 'messages'
            ? 'placeholder: messages injected at runtime'
            : 'section: a list or object passed at call time';
          return (
            <div key={v.name} className={GRID}>
              <div className="flex min-w-0 items-center gap-1.5">
                <code className="truncate font-mono text-[11.5px] text-[color:var(--text-primary)]" title={v.name}>
                  {v.name}
                </code>
                <TypeChip type={v.type} />
              </div>
              <span className="text-[11.5px] text-[color:var(--text-muted)]">
                {note}
              </span>
            </div>
          );
        }

        // A name used only inside a {{#section}} body is a PER-ITEM field: each
        // item supplies it, and the Default here is the fallback for items
        // that omit it. Same row, honest labelling — the declaration itself is
        // load-bearing (it's what lets a missing item field render empty
        // rather than fail) and is not touched.
        const isItemField = innerNames?.has?.(v.name);
        return (
          <div key={v.name} className={GRID}>
            <div className="flex min-w-0 items-center gap-1.5">
              <code className="truncate font-mono text-[11.5px] text-[color:var(--text-primary)]" title={v.name}>
                {v.name}
              </code>
              {isItemField && <TypeChip type="item" />}
            </div>
            <input
              className={controlCls}
              disabled={disabled}
              value={v.default ?? ''}
              onChange={(e) => set(i, { ...v, default: e.target.value === '' ? undefined : e.target.value })}
              placeholder={isItemField ? 'fallback when an item omits it' : 'empty if the caller omits it'}
            />
          </div>
        );
      })}

    </div>
  );
}
